import type { ReactElement } from 'react';
import { useMe, useOrg } from '../api/queries';
import { dateLabel } from '../lib/time';

/**
 * The free month, counted down — one quiet line under the header.
 *
 * Vladimir, 2026-09-26: *"I need them to see a small banner with how many days
 * left of free use. We don't have a mechanism for paying yet. Instead ask
 * users for their company to contact me for agreement"*.
 *
 * Nothing for somebody paid for. Amber for the last week, and once it is over
 * — nothing is broken, so never red: what was recorded is still all theirs to
 * read, and the line says so (the api's `access/free-month.ts` has the rule).
 */
export function FreeMonth(): ReactElement | null {
  const me = useMe();
  const org = useOrg();
  const access = me.data?.access;
  if (!access || access.state === 'PAID') return null;

  const company = org.data?.name ?? 'our company';
  const write = access.contact ? (
    <a href={`mailto:${access.contact}?subject=${encodeURIComponent(`Ptitim for ${company}`)}`}>
      {access.contact}
    </a>
  ) : (
    'us'
  );

  if (access.state === 'FREE') {
    const days = access.daysLeft ?? 0;
    return (
      <div
        className={`notice notice-plan ${days <= 7 ? 'notice-attention' : ''}`}
        role="note"
        aria-label="Free month"
      >
        <span className="grow">
          <strong>
            {days} {days === 1 ? 'day' : 'days'} left
          </strong>{' '}
          of your free month.{' '}
          {access.endsAt ? `To keep Ptitim after ${dateLabel(access.endsAt)}, ask` : 'Ask'} your
          company to write to {write} about an agreement.
        </span>
      </div>
    );
  }
  return (
    <div className="notice notice-plan notice-attention" role="note" aria-label="Free month">
      <span className="grow">
        <strong>
          Ptitim stopped recording for you
          {access.endsAt ? ` on ${dateLabel(access.endsAt)}` : ''}.
        </strong>{' '}
        Everything recorded stays yours to read. To go on, ask your company to write to {write}{' '}
        about an agreement.
      </span>
    </div>
  );
}
